import { SeriesOptionsType } from "highcharts";
import { ChartAxisProps, ChartDataSeries, ChartType } from "../../../components/Chart";
import { ReportingPeriods } from "../../../types";
import { getMonthNameAbbreviated } from "../../../utils/monthNames";
import ApiClient from "../../api";

export type CovenantsCashCoverChartData = {
  series: Array<ChartDataSeries>;
  xAxis: ChartAxisProps;
  yAxis: ChartAxisProps;
};

export type CovenantsCashCoverDetail = {
  financialYear: number;
  quarter: number;
  month: number;
  year: number;
  value: number;
};

export type CovenantsCashCoverDBData = {
  proForma: {
    dataSeries: Array<CovenantsCashCoverDetail>;
  };
  complianceCertificate: {
    dataSeries: Array<CovenantsCashCoverDetail>;
  };
  covenantTest: {
    dataSeries: Array<CovenantsCashCoverDetail>;
  };
};

export const getCovenantsCashCoverChartData = async (
  companyId: number,
  reportingCycle: ReportingPeriods,
  financialYear: number,
  enableConversions: boolean = false,
  unit: string = ""
) => {
  const queryString = `?type=${
    ChartType.COVENANTS_CASH_COVER
  }&companyId=${companyId}&reportingCycle=${reportingCycle}&financialYear=${financialYear}&enableConversions=${
    enableConversions ? "true" : "false"
  }&unit=${unit ? unit : ""}`;

  const seriesFromDB = await ApiClient.get(`/charts${queryString}`);
  if (!seriesFromDB.data) return null;

  let xAxis: ChartAxisProps | null = null;
  const xAxisTitles = await getXAxisTitles(seriesFromDB.data, reportingCycle);

  if (xAxisTitles) {
    xAxis = {
      titles: xAxisTitles,
      colors: [],
    };
  }

  const yAxis: ChartAxisProps = {
    titles: [],
    colors: [],
  };

  const series = await getSeries(seriesFromDB.data, reportingCycle);

  return {
    series,
    xAxis,
    yAxis,
  } as CovenantsCashCoverChartData;
};

const getXAxisTitles = async (data: CovenantsCashCoverDBData, reportingCycle: ReportingPeriods) => {
  let detailToUse: CovenantsCashCoverDetail[];

  // using the first arrays with data
  if (data.complianceCertificate.dataSeries?.length > 0) {
    detailToUse = data.complianceCertificate.dataSeries;
  } else if (data.covenantTest.dataSeries?.length > 0) {
    detailToUse = data.covenantTest.dataSeries;
  } else if (data.proForma.dataSeries?.length > 0) {
    detailToUse = data.proForma.dataSeries;
  } else {
    return [];
  }

  detailToUse = detailToUse.sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));

  if (reportingCycle === ReportingPeriods.Quarterly) {
    let dataToReturn = [];
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)}`);
    }
    return dataToReturn;
  } else if (reportingCycle === ReportingPeriods.Monthly) {
    let dataToReturn = [];
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)}`);
    }
    return dataToReturn;
  } else {
    return [];
  }
};

// Use getSeries to decorate the series with specific elements to the chart.
const getSeries = async (data: CovenantsCashCoverDBData, reportingCycle: ReportingPeriods) => {
  const series: SeriesOptionsType[] = [];
  series.push({
    animation: false,
    name: "Pro Forma",
    data: await getRawDataFromSeries(data.proForma.dataSeries, reportingCycle),
    color: "#4FBCA8",
    type: "column",
    borderRadius: 0,
  });
  series.push({
    animation: false,
    name: "As Per Compliance Certificate",
    data: await getRawDataFromSeries(data.complianceCertificate.dataSeries, reportingCycle),
    color: "#2c796b",
    type: "column",
    borderRadius: 0,
  });
  series.push({
    animation: false,
    name: "Covenant Test",
    data: await getRawDataFromSeries(data.covenantTest.dataSeries, reportingCycle),
    color: "#43ABE3",
    type: "column",
    borderRadius: 0,
  });
  return series;
};

const getRawDataFromSeries = async (data: CovenantsCashCoverDetail[], reportingCycle: ReportingPeriods) => {
  if (!data || data.length === 0) return [];

  let rawDataToReturn = [];

  switch (reportingCycle) {
    case ReportingPeriods.Quarterly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    case ReportingPeriods.Monthly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    default:
      return [];
  }
};
